import React, { useState } from 'react';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardHeader,
  Alert,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { phase1ValidationSchema } from '../../../utils/yupValidators';
import {
  formatCPF,
  formatCEP,
  formatPhone,
  formatRG,
  cleanDigits,
  BRAZILIAN_STATES,
} from '../../../utils/brazilianFields';
import { companiesService } from '../../../services/companiesService';
import { cepService } from '../../../services/cepService';

interface Phase1PartnersFormProps {
  onSubmit: (values: any) => void;
  initialValues?: any;
  isSubmitting: boolean;
}

interface Partner {
  id?: string; // Optional - only present when editing existing partner
  name: string;
  cpf: string;
  phone: string;
  senhaGov: string; // NEW: Gov.br password (required, encrypted on backend)
  dataNascimento: string; // NEW: Birth date (required, format: YYYY-MM-DD)
  rg: string;
  email: string;
  entidadeClasse: string; // NEW: Professional entity (optional)
  isActive: boolean; // NEW: Partner active status
  isMajoritario: boolean; // NEW: Partner is majority (only one can be true)
  participacao: number; // NEW: Participation percentage (0-100)
  administrativo: boolean; // NEW: Administrative partner (only one can be true)
  address: {
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
  } | null;
}

const emptyPartner: Partner = {
  name: '',
  cpf: '',
  phone: '',
  senhaGov: '',
  dataNascimento: '',
  rg: '',
  email: '',
  entidadeClasse: '',
  isActive: true, // Default to active
  isMajoritario: false, // Default to not majority
  participacao: 0, // Default to 0%
  administrativo: false, // Default to not administrative
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    cep: '',
  },
};

const Phase1PartnersForm: React.FC<Phase1PartnersFormProps> = ({
  onSubmit,
  initialValues,
  isSubmitting,
}) => {
  const { t} = useTranslation();
  const [loadingCep, setLoadingCep] = useState<{ [key: number]: boolean }>({});
  const [validatingCpf, setValidatingCpf] = useState<{ [key: number]: boolean }>({});
  const [showSenhaGov, setShowSenhaGov] = useState<{ [key: number]: boolean }>({});
  const [participacaoInput, setParticipacaoInput] = useState<{ [key: number]: string }>({});
  const [participacaoError, setParticipacaoError] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: initialValues || {
      partners: [{ ...emptyPartner }],
    },
    enableReinitialize: true, // Allow form to reinitialize when initialValues change
    validationSchema: phase1ValidationSchema,
    onSubmit: (values) => {
      // Clean all fields before submission
      const cleanedValues = {
        partners: values.partners.map((p: Partner) => ({
          ...p,
          cpf: cleanDigits(p.cpf),
          phone: cleanDigits(p.phone),
          address: p.address
            ? {
                ...p.address,
                cep: cleanDigits(p.address.cep),
              }
            : null,
        })),
      };
      onSubmit(cleanedValues);
    },
  });

  // Handle form submission with validation check
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Trigger validation on all fields
    const errors = await formik.validateForm();
    formik.setTouched({
      partners: formik.values.partners.map(() => ({
        name: true,
        cpf: true,
        phone: true,
        senhaGov: true,
        dataNascimento: true,
        rg: true,
        email: true,
        entidadeClasse: true,
        isActive: true,
        isMajoritario: true,
      }))
    });

    // Validate total participation equals 100% (run this BEFORE checking other errors)
    const totalParticipacao = formik.values.partners.reduce(
      (sum: number, p: Partner) => sum + (p.participacao || 0),
      0
    );

    let hasParticipacaoError = false;
    if (totalParticipacao !== 100 && totalParticipacao !== 0) {
      setParticipacaoError(true);
      hasParticipacaoError = true;
      toast.warning(
        t('A participação de todos os sócios deve somar 100%. Total atual: {{total}}%', {
          total: totalParticipacao.toFixed(2).replace('.', ',')
        })
      );
    } else {
      // Clear error if validation passes
      setParticipacaoError(false);
    }

    // Check if there are validation errors
    if (Object.keys(errors).length > 0) {
      toast.error(t('Por favor, preencha todos os campos obrigatórios corretamente.'));
      return;
    }

    // If participation validation failed, don't submit
    if (hasParticipacaoError) {
      return;
    }

    // If all validations pass, submit the form
    formik.handleSubmit(e);
  };

  // CPF Formatting and Validation
  const handleCpfChange = async (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatCPF(e.target.value);
    formik.setFieldValue(`partners.${index}.cpf`, formatted);

    // Validate CPF when complete
    if (cleanDigits(formatted).length === 11) {
      setValidatingCpf({ ...validatingCpf, [index]: true });
      try {
        const result = await companiesService.validateCpf(formatted);
        if (!result.isValid) {
          formik.setFieldError(
            `partners.${index}.cpf`,
            result.errorMessage || 'CPF inválido'
          );
        }
      } catch (error) {
        console.error('CPF validation error:', error);
      } finally {
        setValidatingCpf({ ...validatingCpf, [index]: false });
      }
    }
  };

  // RG Formatting
  const handleRgChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatRG(e.target.value);
    formik.setFieldValue(`partners.${index}.rg`, formatted);
  };

  // Phone Formatting
  const handlePhoneChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    formik.setFieldValue(`partners.${index}.phone`, formatted);
  };

  // CEP Lookup
  const handleCepChange = async (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatCEP(e.target.value);
    formik.setFieldValue(`partners.${index}.address.cep`, formatted);

    // Lookup address when CEP is complete
    if (cleanDigits(formatted).length === 8) {
      setLoadingCep({ ...loadingCep, [index]: true });
      try {
        const address = await cepService.getAddressByCep(formatted);

        if (address) {
          // Update all address fields at once using setValues
          const currentValues = formik.values;
          const updatedPartners = [...currentValues.partners];
          updatedPartners[index] = {
            ...updatedPartners[index],
            address: {
              ...updatedPartners[index].address,
              street: address.street || '',
              neighborhood: address.neighborhood || '',
              city: address.city || '',
              state: address.state || '',
              cep: formatted,
            }
          };

          formik.setValues({
            ...currentValues,
            partners: updatedPartners
          });
        }
      } catch (error: any) {
        console.error('CEP lookup error:', error);
        toast.warning(t('CEP não encontrado. Por favor, preencha o endereço manualmente.'));
      } finally {
        setLoadingCep({ ...loadingCep, [index]: false });
      }
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleFormSubmit}>
        <Row>
          <Col lg={12}>
            <Alert color="info" className="mb-4">
              <i className="ri-information-line me-2"></i>
              {t(
                'Fase 1: Preencha os dados dos sócios. Pelo menos um sócio é obrigatório. Você pode adicionar até 3 sócios.'
              )}
            </Alert>
          </Col>
        </Row>

        {/* Partners - MOVED TO TOP */}
        <FieldArray name="partners">
          {({ push, remove }) => (
            <>
              {formik.values.partners.map((partner: Partner, index: number) => (
                <Card key={index} className="mb-4">
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="ri-user-3-line me-2"></i>
                      {t(`Sócio ${index + 1}`)}
                      {index === 0 && (
                        <span className="badge bg-primary ms-2">{t('Obrigatório')}</span>
                      )}
                    </h5>
                    {index > 0 && (
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => remove(index)}
                      >
                        <i className="ri-delete-bin-line"></i> {t('Remover')}
                      </Button>
                    )}
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for={`partners.${index}.name`}>
                            {t('Nome Completo')} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id={`partners.${index}.name`}
                            name={`partners.${index}.name`}
                            value={partner.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                (formik.touched.partners as any)?.[index]?.name &&
                                (formik.errors.partners as any)?.[index]?.name
                              )
                            }
                          />
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.name}
                          </FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for={`partners.${index}.cpf`}>
                            {t('CPF')} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id={`partners.${index}.cpf`}
                            name={`partners.${index}.cpf`}
                            value={partner.cpf}
                            onChange={(e) => handleCpfChange(index, e)}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                (formik.touched.partners as any)?.[index]?.cpf &&
                                (formik.errors.partners as any)?.[index]?.cpf
                              )
                            }
                            placeholder="000.000.000-00"
                          />
                          {validatingCpf[index] && (
                            <small className="text-muted">
                              <i className="ri-loader-4-line rotating me-1"></i>
                              {t('Validando...')}
                            </small>
                          )}
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.cpf}
                          </FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for={`partners.${index}.rg`}>{t('RG')}</Label>
                          <Input
                            id={`partners.${index}.rg`}
                            name={`partners.${index}.rg`}
                            value={partner.rg}
                            onChange={(e) => handleRgChange(index, e)}
                            onBlur={formik.handleBlur}
                            placeholder="00.000.000-0"
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.email`}>{t('Email')}</Label>
                          <Input
                            type="email"
                            id={`partners.${index}.email`}
                            name={`partners.${index}.email`}
                            value={partner.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            autoComplete="off"
                            invalid={
                              !!(
                                (formik.touched.partners as any)?.[index]?.email &&
                                (formik.errors.partners as any)?.[index]?.email
                              )
                            }
                          />
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.email}
                          </FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.phone`}>
                            {t('Telefone')} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id={`partners.${index}.phone`}
                            name={`partners.${index}.phone`}
                            value={partner.phone}
                            onChange={(e) => handlePhoneChange(index, e)}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                (formik.touched.partners as any)?.[index]?.phone &&
                                (formik.errors.partners as any)?.[index]?.phone
                              )
                            }
                            placeholder="(00) 00000-0000"
                          />
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.phone}
                          </FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.dataNascimento`}>
                            {t('Data de Nascimento')} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="date"
                            id={`partners.${index}.dataNascimento`}
                            name={`partners.${index}.dataNascimento`}
                            value={partner.dataNascimento}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                (formik.touched.partners as any)?.[index]?.dataNascimento &&
                                (formik.errors.partners as any)?.[index]?.dataNascimento
                              )
                            }
                          />
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.dataNascimento}
                          </FormFeedback>
                        </FormGroup>
                      </Col>

                      {/* Address Section */}
                      <Col xs={12}>
                        <hr className="my-3" />
                        <h6 className="text-muted mb-3">
                          <i className="ri-map-pin-line me-2"></i>
                          {t('Endereço (Opcional)')}
                        </h6>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.cep`}>{t('CEP')}</Label>
                          <Input
                            id={`partners.${index}.address.cep`}
                            name={`partners.${index}.address.cep`}
                            value={partner.address?.cep || ''}
                            onChange={(e) => handleCepChange(index, e)}
                            onBlur={formik.handleBlur}
                            placeholder="00000-000"
                          />
                          {loadingCep[index] && (
                            <small className="text-muted">
                              <i className="ri-loader-4-line rotating me-1"></i>
                              {t('Buscando endereço...')}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.street`}>
                            {t('Rua')}
                          </Label>
                          <Input
                            id={`partners.${index}.address.street`}
                            name={`partners.${index}.address.street`}
                            value={partner.address?.street || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.number`}>
                            {t('Número')} {partner.address?.cep && <span className="text-danger">*</span>}
                          </Label>
                          <Input
                            id={`partners.${index}.address.number`}
                            name={`partners.${index}.address.number`}
                            value={partner.address?.number || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={!!((formik.touched.partners as any)?.[index]?.address?.number && (formik.errors.partners as any)?.[index]?.address?.number)}
                          />
                          <FormFeedback>{(formik.errors.partners as any)?.[index]?.address?.number}</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.complement`}>
                            {t('Complemento')}
                          </Label>
                          <Input
                            id={`partners.${index}.address.complement`}
                            name={`partners.${index}.address.complement`}
                            value={partner.address?.complement || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.neighborhood`}>
                            {t('Bairro')}
                          </Label>
                          <Input
                            id={`partners.${index}.address.neighborhood`}
                            name={`partners.${index}.address.neighborhood`}
                            value={partner.address?.neighborhood || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.city`}>
                            {t('Cidade')}
                          </Label>
                          <Input
                            id={`partners.${index}.address.city`}
                            name={`partners.${index}.address.city`}
                            value={partner.address?.city || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={1}>
                        <FormGroup>
                          <Label for={`partners.${index}.address.state`}>
                            {t('UF')}
                          </Label>
                          <Input
                            type="select"
                            id={`partners.${index}.address.state`}
                            name={`partners.${index}.address.state`}
                            value={partner.address?.state || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">-</option>
                            {BRAZILIAN_STATES.map((state) => (
                              <option key={state} value={state}>
                                {state}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Administrative Credentials and Participation Row */}
                    <Row className="mt-4 pt-3 border-top">
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.senhaGov`}>
                            {t('Senha Gov.br')} <span className="text-danger">*</span>
                          </Label>
                          <div className="position-relative">
                            <Input
                              type={showSenhaGov[index] ? 'text' : 'password'}
                              id={`partners.${index}.senhaGov`}
                              name={`partners.${index}.senhaGov`}
                              value={partner.senhaGov}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              autoComplete="new-password"
                              invalid={
                                !!(
                                  (formik.touched.partners as any)?.[index]?.senhaGov &&
                                  (formik.errors.partners as any)?.[index]?.senhaGov
                                )
                              }
                              placeholder={t('Senha do portal Gov.br')}
                              className="pe-5"
                            />
                            <button
                              type="button"
                              className="btn btn-link position-absolute end-0 top-0 text-muted"
                              onClick={() => setShowSenhaGov({ ...showSenhaGov, [index]: !showSenhaGov[index] })}
                              style={{ textDecoration: 'none' }}
                            >
                              <i className={showSenhaGov[index] ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                            </button>
                          </div>
                          <FormFeedback>
                            {(formik.errors.partners as any)?.[index]?.senhaGov}
                          </FormFeedback>
                          <small className="form-text text-muted">
                            {t('Será armazenada de forma criptografada')}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.entidadeClasse`}>
                            {t('Entidade de Classe')}
                          </Label>
                          <Input
                            id={`partners.${index}.entidadeClasse`}
                            name={`partners.${index}.entidadeClasse`}
                            value={partner.entidadeClasse}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder={t('Ex: CRC, CRM, OAB, etc.')}
                          />
                          <small className="form-text text-muted">
                            {t('Conselho profissional, se aplicável')}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.participacao`}>
                            {t('Participação')} (%)
                          </Label>
                          <Input
                            type="text"
                            id={`partners.${index}.participacao`}
                            name={`partners.${index}.participacao`}
                            value={
                              participacaoInput[index] !== undefined
                                ? participacaoInput[index]
                                : String(partner.participacao || '').replace('.', ',')
                            }
                            disabled={partner.isMajoritario}
                            invalid={
                              participacaoError &&
                              (!partner.isMajoritario || formik.values.partners.every((p: Partner) => p.isMajoritario))
                            }
                            onChange={(e) => {
                              // Clear error when user starts typing
                              if (participacaoError) {
                                setParticipacaoError(false);
                              }

                              const inputValue = e.target.value;
                              // Allow empty string or valid numbers with comma or period as decimal separator
                              if (inputValue === '' || /^\d*[,.]?\d{0,6}$/.test(inputValue)) {
                                // Only store raw input if it ends with decimal separator (typing "0," or "10,")
                                if (inputValue.endsWith(',') || inputValue.endsWith('.')) {
                                  setParticipacaoInput({ ...participacaoInput, [index]: inputValue });
                                } else {
                                  // Clear raw input for complete numbers
                                  const newInput = { ...participacaoInput };
                                  delete newInput[index];
                                  setParticipacaoInput(newInput);
                                }

                                // Convert comma to period for parsing
                                const normalizedValue = inputValue.replace(',', '.');
                                const newValue = normalizedValue === '' || normalizedValue.endsWith('.') ?
                                  parseFloat(normalizedValue.slice(0, -1)) || 0 :
                                  parseFloat(normalizedValue) || 0;

                                // Validate range and that it's a valid number
                                if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
                                  // Check if this value would make this partner majoritario
                                  const shouldBeMajoritario = newValue >= 50;

                                  // Get other partners (excluding current one)
                                  const otherPartners = formik.values.partners.filter(
                                    (_: Partner, idx: number) => idx !== index
                                  );

                                  const otherCount = otherPartners.length;

                                  if (otherCount > 0) {
                                    const remaining = 100 - newValue;
                                    const perPartner = remaining / otherCount;

                                    const updatedPartners = formik.values.partners.map((p: Partner, idx: number) => {
                                      if (idx === index) {
                                        // Auto-mark/unmark as majoritario based on participation
                                        if (shouldBeMajoritario && !p.isMajoritario) {
                                          toast.info(t('Participação >= 50%. Marcado como majoritário.'));
                                        }
                                        return { ...p, participacao: newValue, isMajoritario: shouldBeMajoritario };
                                      }
                                      // When marking current as majoritario, unmark all others and redistribute
                                      if (shouldBeMajoritario) {
                                        return { ...p, participacao: perPartner, isMajoritario: false };
                                      }
                                      // If current is not majoritario, only update non-majoritario partners
                                      if (!p.isMajoritario) {
                                        return { ...p, participacao: perPartner };
                                      }
                                      return p;
                                    });
                                    formik.setFieldValue('partners', updatedPartners);
                                  } else {
                                    // Just update this partner
                                    if (shouldBeMajoritario && !partner.isMajoritario) {
                                      toast.info(t('Participação >= 50%. Marcado como majoritário.'));
                                    }
                                    formik.setFieldValue(`partners.${index}.participacao`, newValue);
                                    formik.setFieldValue(`partners.${index}.isMajoritario`, shouldBeMajoritario);
                                  }
                                }
                              }
                            }}
                            onBlur={() => {
                              // Clear raw input on blur to sync with formik value
                              const newInput = { ...participacaoInput };
                              delete newInput[index];
                              setParticipacaoInput(newInput);
                            }}
                            placeholder="0"
                          />
                          {participacaoError && (!partner.isMajoritario || formik.values.partners.every((p: Partner) => p.isMajoritario)) && (
                            <FormFeedback>
                              {t('A soma da participação deve ser 100%. Total atual: {{total}}%', {
                                total: formik.values.partners.reduce((sum: number, p: Partner) => sum + (p.participacao || 0), 0).toFixed(2).replace('.', ',')
                              })}
                            </FormFeedback>
                          )}
                          <small className="text-muted">
                            {t('Total')}: {formik.values.partners.reduce((sum: number, p: Partner) => sum + (p.participacao || 0), 0).toFixed(2).replace('.', ',')}%
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Status Toggles Row */}
                    <Row className="mt-3">
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.isActive`}>
                            {t('Ativo')}
                          </Label>
                          <div className="form-check form-switch form-switch-lg">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id={`partners.${index}.isActive`}
                              name={`partners.${index}.isActive`}
                              checked={partner.isActive}
                              onChange={(e) => {
                                formik.setFieldValue(`partners.${index}.isActive`, e.target.checked);
                              }}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.isMajoritario`}>
                            {t('Majoritário')} <span className="text-danger">*</span>
                          </Label>
                          <div className="form-check form-switch form-switch-lg">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id={`partners.${index}.isMajoritario`}
                              name={`partners.${index}.isMajoritario`}
                              checked={partner.isMajoritario}
                              onChange={(e) => {
                                // When marking this partner as majoritario, validate participation >= 50%
                                if (e.target.checked) {
                                  const currentParticipacao = partner.participacao || 0;
                                  if (currentParticipacao < 50) {
                                    toast.warning(t('Sócio majoritário deve ter participação de no mínimo 50%'));
                                    return;
                                  }

                                  // Calculate remaining percentage to distribute among others
                                  const otherNonMajoritarioPartners = formik.values.partners.filter(
                                    (_: Partner, idx: number) => idx !== index
                                  );
                                  const otherCount = otherNonMajoritarioPartners.length;

                                  if (otherCount > 0) {
                                    const remaining = 100 - currentParticipacao;
                                    const perPartner = remaining / otherCount;

                                    // Unmark all others and redistribute participation
                                    const updatedPartners = formik.values.partners.map((p: Partner, idx: number) => {
                                      if (idx === index) {
                                        return { ...p, isMajoritario: true };
                                      }
                                      return { ...p, isMajoritario: false, participacao: perPartner };
                                    });
                                    formik.setFieldValue('partners', updatedPartners);
                                  } else {
                                    formik.setFieldValue(`partners.${index}.isMajoritario`, true);
                                  }
                                } else {
                                  // When unmarking, redistribute participation equally
                                  const partnersCount = formik.values.partners.length;
                                  const perPartner = 100 / partnersCount;

                                  const updatedPartners = formik.values.partners.map((p: Partner) => ({
                                    ...p,
                                    isMajoritario: false,
                                    participacao: perPartner
                                  }));
                                  formik.setFieldValue('partners', updatedPartners);
                                }
                              }}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for={`partners.${index}.administrativo`}>
                            {t('Administrativo')}
                          </Label>
                          <div className="form-check form-switch form-switch-lg">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id={`partners.${index}.administrativo`}
                              name={`partners.${index}.administrativo`}
                              checked={partner.administrativo}
                              onChange={(e) => {
                                // When marking this partner as administrativo, unmark all others
                                if (e.target.checked) {
                                  const updatedPartners = formik.values.partners.map((p: Partner, idx: number) => ({
                                    ...p,
                                    administrativo: idx === index
                                  }));
                                  formik.setFieldValue('partners', updatedPartners);
                                } else {
                                  formik.setFieldValue(`partners.${index}.administrativo`, false);
                                }
                              }}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}

              {formik.values.partners.length < 3 && (
                <div className="text-center mb-4">
                  <Button
                    color="success"
                    outline
                    onClick={() => {
                      // If this is the first partner or no partner is marked as majoritario, mark the new one as majoritario
                      const hasMajoritario = formik.values.partners.some((p: Partner) => p.isMajoritario);
                      push({ ...emptyPartner, isMajoritario: !hasMajoritario });
                    }}
                  >
                    <i className="ri-add-line me-1"></i>
                    {t('Adicionar Sócio')}
                  </Button>
                </div>
              )}
            </>
          )}
        </FieldArray>

        {/* Form Actions */}
        <div className="d-flex justify-content-end gap-2">
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line rotating me-1"></i>
                {t('Salvando...')}
              </>
            ) : (
              <>
                {t('Próximo')}
                <i className="ri-arrow-right-line ms-1"></i>
              </>
            )}
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
};

export default Phase1PartnersForm;
